// 商品の一括追加（貼り付け）＝ Reddit 現場オペ4点目の画面。
//
// 土台（`41f0c70`）＝ pastedLines.ts（列判定）＋
// `GET /purchase-orders/variants?codes=`（一括突合・barcode 優先）。
// プレビュー組み立ての純ロジックは utils/pasteAddPreview.ts。
//
// 🔴 プレビューは確認画面ではなく【修正画面】（2026-08-02 月次ログの方針）＝
//    数量列の選び直し・数量の直接編集・行の除外がこの場でできる。
//    「気づけるだけで直せない警告」は無意味。
// 🔴 突合できなかった行は黙って落とさず、件数と内容を見せる（MoqPasteImport と同型）。
// 追加の意味論は単発の「追加」と同じ＝この注文書だけの一時明細
// （仕入先設定・次回以降の発注書には影響しない）。
import {
  Badge,
  Banner,
  BlockStack,
  Box,
  Button,
  Checkbox,
  InlineStack,
  Select,
  Text,
  TextField,
} from "@shopify/polaris";
import { useState } from "react";

import {
  PASTE_LOOKUP_CODE_LIMIT,
  lookupOrderableVariantsByCodes,
  type PurchaseOrderLine,
} from "../../api/purchase_orders";
import {
  canonicalStockyPublicBigIntId,
  isStockyPublicBigIntId,
  type StockyPublicBigIntId,
} from "../../api/stocky_public_ids";
import { csvErrorMessage } from "../csvImportSpec";
import { t } from "../../lib/i18n";
import { parsePastedGrid } from "../../utils/pastedLines";
import {
  buildPastePreview,
  collectAddItems,
  collectPasteCodes,
  parseRowQty,
  withQtyColumn,
  type PastePreviewData,
  type PastePreviewRow,
  type PastedProductAdd,
} from "../../utils/pasteAddPreview";

type Props = {
  /** すでに明細にある variant（自動明細＋任意追加済み）。重複追加を防ぐ。 */
  existingVariantIds: ReadonlySet<StockyPublicBigIntId>;
  /** 配送先。任意追加行の在庫・入荷予定はこの拠点基準で返る。 */
  locationId: StockyPublicBigIntId | null;
  /** まとめて追加。親が addedLines と数量編集へ反映する。 */
  onAdd: (items: PastedProductAdd[]) => void;
};

/** 単発追加の候補ボタンと同じ形＝商品名（SKU）。 */
function lineLabel(line: PurchaseOrderLine): string {
  return `${line.title ?? t("商品名なし")}${line.sku ? `（${line.sku}）` : ""}`;
}

function qtyColumnLabel(preview: PastePreviewData, column: number): string {
  const header = preview.headerRow?.[column]?.trim();
  return header
    ? t("{n} 列目（{header}）", { n: column + 1, header })
    : t("{n} 列目", { n: column + 1 });
}

export function PasteAddProducts({
  existingVariantIds,
  locationId,
  onAdd,
}: Props) {
  const [text, setText] = useState("");
  const [preview, setPreview] = useState<PastePreviewData | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [isBusy, setIsBusy] = useState(false);

  const handleChange = (value: string) => {
    setText(value);
    // 貼り直したら前の突合結果は捨てる（別内容の件数で「まとめて追加」を残さない）。
    setPreview(null);
    setError(null);
  };

  const handlePreview = async () => {
    const grid = parsePastedGrid(text);
    const codes = collectPasteCodes(grid);
    if (codes.length === 0) {
      setPreview(null);
      setError(
        t(
          "貼り付けから商品コードを読み取れませんでした。SKU または バーコード（JAN）を含めて貼り付けてください。",
        ),
      );
      return;
    }
    if (codes.length > PASTE_LOOKUP_CODE_LIMIT) {
      // 🔴 黙って切らない（backend の 422 と同じ思想）＝上限と実数を言って直してもらう。
      setPreview(null);
      setError(
        t(
          "貼り付けが大きすぎます（{count} 値）。一度に突合できるのは {max} 値までです。何回かに分けて貼り付けてください。",
          { count: codes.length, max: PASTE_LOOKUP_CODE_LIMIT },
        ),
      );
      return;
    }
    setIsBusy(true);
    setError(null);
    try {
      const lines = await lookupOrderableVariantsByCodes(codes, locationId);
      const built = buildPastePreview(grid, lines, existingVariantIds);
      if (built === null) {
        setPreview(null);
        setError(
          t(
            "貼り付けた内容に、この店の商品と一致する SKU・バーコードが見つかりませんでした。Shopify の商品に SKU か バーコードが登録されているか、同期が済んでいるかを確認してください。",
          ),
        );
        return;
      }
      setPreview(built);
    } catch (err) {
      setPreview(null);
      setError(csvErrorMessage(err));
    } finally {
      setIsBusy(false);
    }
  };

  const updateRow = (key: number, patch: Partial<PastePreviewRow>) =>
    setPreview((current) =>
      current === null
        ? current
        : {
            ...current,
            rows: current.rows.map((row) =>
              row.key === key ? { ...row, ...patch } : row,
            ),
          },
    );

  const handleQtyColumnChange = (value: string) =>
    setPreview((current) =>
      current === null
        ? current
        : withQtyColumn(current, value === "" ? null : Number(value)),
    );

  // ボタンの件数＝追加対象の商品数（同じ商品に解決した行は1件と数える）。
  // 数量が不正な行も件数には含める＝件数を0に落とすと「チェックしているのに0」に
  // 見える。押せない理由は行のエラー表示と disabled で伝える。
  const includedCount = new Set(
    (preview?.rows ?? [])
      .filter((row) => row.included && !row.alreadyInOrder)
      .map((row) => canonicalStockyPublicBigIntId(row.selectedVariantId)),
  ).size;
  const hasInvalidQty = (preview?.rows ?? []).some(
    (row) =>
      row.included && !row.alreadyInOrder && parseRowQty(row.qtyInput) === null,
  );

  const handleAdd = () => {
    if (preview === null || includedCount === 0 || hasInvalidQty) return;
    const items = collectAddItems(preview.rows, existingVariantIds);
    if (items.length === 0) return;
    onAdd(items);
    setText("");
    setPreview(null);
    setError(null);
  };

  const qtyColumnOptions = preview
    ? [
        ...preview.qtyCandidates.map((column) => ({
          label: qtyColumnLabel(preview, column),
          value: String(column),
        })),
        { label: t("数量の列なし（すべて 1）"), value: "" },
      ]
    : [];

  return (
    <BlockStack gap="300">
      <Text as="h3" variant="headingSm">
        {t("貼り付けでまとめて追加")}
      </Text>
      <Text as="p" variant="bodySm" tone="subdued">
        {t(
          "表計算ソフトや仕入先の注文書から、SKU・バーコード（JAN）を含む表をそのまま貼り付けると、複数の商品を一度に追加できます。列の順番は問いません。",
        )}
      </Text>
      <TextField
        label={t("貼り付け欄")}
        value={text}
        onChange={handleChange}
        multiline={5}
        autoComplete="off"
        placeholder={"HONEY-500\t2\nOLIVE-200\t3"}
      />

      {error && (
        <Banner tone="critical" title={t("突合できませんでした")}>
          <Text as="p">{error}</Text>
        </Banner>
      )}

      {preview && (
        <BlockStack gap="300">
          <Banner
            tone={preview.rows.length > 0 ? "info" : "warning"}
            title={t("{count} 行を商品と突合しました", {
              count: preview.rows.length,
            })}
          >
            <Text as="p">
              {preview.rows.length > 0
                ? t(
                    "数量と追加する行をここで直せます。よければ「まとめて追加」を押してください。",
                  )
                : t(
                    "追加できる行がありません。SKU・バーコードの列を含めて貼り付け直してください。",
                  )}
            </Text>
          </Banner>

          {preview.qtyCandidates.length > 0 && (
            <Box maxWidth="320px">
              <Select
                label={t("数量の列")}
                options={qtyColumnOptions}
                value={
                  preview.qtyColumn === null ? "" : String(preview.qtyColumn)
                }
                onChange={handleQtyColumnChange}
                helpText={t(
                  "数量の読み違い（単価などを数量と読んだ場合）は、ここで列を選び直すと全行に反映されます。",
                )}
              />
            </Box>
          )}

          {preview.rows.map((row) => {
            const selectedLine =
              row.candidates.find(
                (candidate) =>
                  canonicalStockyPublicBigIntId(candidate.variant_id) ===
                  canonicalStockyPublicBigIntId(row.selectedVariantId),
              ) ?? row.candidates[0];
            const qtyInvalid =
              row.included &&
              !row.alreadyInOrder &&
              parseRowQty(row.qtyInput) === null;
            return (
              <InlineStack key={row.key} gap="200" blockAlign="center">
                <Checkbox
                  label={t("{code} を追加する", { code: row.code })}
                  labelHidden
                  checked={row.included && !row.alreadyInOrder}
                  disabled={row.alreadyInOrder}
                  onChange={(checked) =>
                    updateRow(row.key, { included: checked })
                  }
                />
                <Box minWidth="240px">
                  {row.candidates.length > 1 ? (
                    // 同じコードに複数商品（共有バーコード等）＝推測で決めず選ばせる。
                    <Select
                      label={t("追加する商品")}
                      labelHidden
                      options={row.candidates.map((candidate) => ({
                        label: lineLabel(candidate),
                        value: String(candidate.variant_id),
                      }))}
                      value={String(row.selectedVariantId)}
                      onChange={(value) => {
                        if (!isStockyPublicBigIntId(value)) return;
                        updateRow(row.key, {
                          selectedVariantId:
                            canonicalStockyPublicBigIntId(value),
                        });
                      }}
                      disabled={row.alreadyInOrder}
                    />
                  ) : (
                    <Text as="span" variant="bodySm">
                      {lineLabel(selectedLine)}
                    </Text>
                  )}
                </Box>
                <Box maxWidth="96px">
                  <TextField
                    label={t("数量")}
                    labelHidden
                    value={row.qtyInput}
                    onChange={(value) =>
                      updateRow(row.key, { qtyInput: value })
                    }
                    autoComplete="off"
                    disabled={row.alreadyInOrder}
                    error={
                      qtyInvalid
                        ? t("数量は 1 以上の整数で入力してください")
                        : undefined
                    }
                  />
                </Box>
                {row.alreadyInOrder && (
                  <Badge tone="attention">{t("すでに明細にあります")}</Badge>
                )}
              </InlineStack>
            );
          })}

          {preview.unmatched.length > 0 && (
            <Banner
              tone="warning"
              title={t("突合できなかった行が {count} 行あります", {
                count: preview.unmatched.length,
              })}
            >
              <Text as="p">
                {t("一致する商品が見つからなかった行: {codes}", {
                  codes: preview.unmatched.join(", "),
                })}
              </Text>
              <Text as="p">
                {t(
                  "同期がまだか、Shopify の商品に SKU・バーコードが登録されていない可能性があります。",
                )}
              </Text>
            </Banner>
          )}
        </BlockStack>
      )}

      <InlineStack gap="200">
        <Button
          onClick={handlePreview}
          loading={isBusy}
          disabled={text.trim() === "" || isBusy}
        >
          {t("貼り付け内容を確認")}
        </Button>
        {preview && (
          <Button
            variant="primary"
            onClick={handleAdd}
            disabled={includedCount === 0 || hasInvalidQty}
          >
            {t("{count} 商品をまとめて追加", { count: includedCount })}
          </Button>
        )}
      </InlineStack>
    </BlockStack>
  );
}
